/**
 * The panel that shows an invite or reset token — ONCE.
 *
 * ⭐⭐ ITS ENTIRE JOB IS TO MAKE "ONCE" OBVIOUS. There is no mailer in this system, so the admin has
 *   to hand this over out of band, and the API genuinely cannot produce it again — only its SHA-256
 *   is stored. An admin who closes this panel assuming they can look it up later has to issue a new
 *   one, which invalidates the first and confuses the recipient.
 *   ⇒ The warning is part of the component rather than a line of copy somebody might reword away.
 *
 * ⛔ NOT auto-dismissed on a timer, and not closed by clicking outside. Both are the standard
 *   treatment for a toast and both would destroy a value that cannot be recovered.
 */
import { useState } from 'react';

export function OneTimeSecret({
  title,
  token,
  expiresAt,
  onDismiss,
}: {
  title: string;
  token: string;
  expiresAt: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      // ⭐ Clipboard access is denied in plenty of legitimate contexts (insecure origin, permissions
      //   policy, an older browser). The token is on screen and selectable, so failing to copy is an
      //   inconvenience — swallowing it silently is right, because there is nothing to fix and the
      //   user can still complete the task.
      setCopied(false);
    }
  };

  return (
    <div
      role="alert"
      className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-xs">
        Shown once and never again — only its hash is stored. Copy it now and hand it over directly;
        PlanHub cannot email it and cannot show it to you a second time.
      </p>

      <code className="block break-all rounded bg-black/10 px-2 py-1.5 font-mono text-xs dark:bg-black/30">
        {token}
      </code>

      <p className="text-xs opacity-80">Expires {new Date(expiresAt).toLocaleString()}</p>

      <div className="flex gap-2">
        <button
          className="h-9 rounded-md border border-current px-3 text-xs font-medium"
          onClick={copy}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          className="h-9 rounded-md border border-current px-3 text-xs font-medium"
          onClick={onDismiss}
        >
          I have saved it
        </button>
      </div>
    </div>
  );
}
